import React, { useEffect, useState } from "react";
import { Button, Form, Input, Select, Upload, message, Spin } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import { useHistory, useLocation } from "react-router-dom";
import axios from "axios";
import * as pdfjsLib from "pdfjs-dist/build/pdf";
import "pdfjs-dist/build/pdf.worker"; // side effect import!
pdfjsLib.GlobalWorkerOptions.workerSrc =
    "//cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";

export default function ProposalUpload() {
    const history = useHistory();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const [form] = Form.useForm();

    const [pdfContents, setPdfContents] = useState("");
    const [pages, setPages] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // PDF 텍스트 추출 함수
    const extractTextFromPDF = async (file) => {
        const fileReader = new FileReader();
        return new Promise((resolve, reject) => {
            fileReader.onload = async () => {
                try {
                    const typedArray = new Uint8Array(fileReader.result);
                    const pdf = await pdfjsLib.getDocument({ data: typedArray }).promise;
                    const tempPages = [];
                    for (let i = 1; i <= pdf.numPages; i++) {
                        const page = await pdf.getPage(i);
                        const content = await page.getTextContent();
                        const strings = content.items.map(item => item.str);
                        const text = strings.join(" ");
                        tempPages.push({ page: i, contents: text });
                    }
                    const allText = tempPages.map(p => p.contents).join("\n");
                    resolve({ allText, tempPages });
                } catch (err) {
                    reject(err);
                }
            };
            fileReader.onerror = reject;
            fileReader.readAsArrayBuffer(file);
        });
    };

    // PDF 업로드 설정 (PDF만, 1개만)
    const uploadProps = {
        name: "file",
        multiple: false,
        accept: ".pdf",
        maxCount: 1,
        beforeUpload: async (file) => {
            if (file.type !== "application/pdf") {
                message.error("PDF 파일만 업로드할 수 있습니다.");
                return false;
            }
            try {
                const { allText, tempPages } = await extractTextFromPDF(file);
                setPdfContents(allText);
                setPages(tempPages);
                message.success(`${file.name} PDF 텍스트 추출 완료`);
            } catch (err) {
                message.error("PDF 텍스트 추출 실패: " + err.message);
            }
            // 실제 업로드는 서버에 보내지 않음
            return false;
        }
    };

    useEffect(() => {
        const id = query.get("id");
        if (id) {
            axios.get(`${process.env.REACT_APP_API_BASE_URL}/proposal/` + id).then((res) => {
                const data = res.data;
                setPdfContents(data.contents);
                setPages(data.pages || []);
                form.setFieldsValue({
                    subCategory: data.subCategory,
                    year: data.year,
                    name: data.name,
                    tags: data.tags ? data.tags.join(",") : "",
                    file: [{
                        uid: "-1",
                        name: "기존PDF.pdf",
                        status: "done",
                        url: data.path || "",
                        response: { url: data.path || "" }
                    }],
                });
            });
        }
    }, [location.search, form, query]);

    const normFile = (e) => (Array.isArray(e) ? e : e?.fileList);

    const handleSubmit = async (values) => {
        const id = query.get("id");
        const tagsArr = values.tags
            ? values.tags.split(",").map(t => t.trim()).filter(Boolean)
            : [];

        const formData = new FormData();
        formData.append("id", id || "");
        formData.append("subCategory", values.subCategory || "");
        formData.append("year", values.year || "");
        formData.append("name", values.name || "");
        formData.append("tags", JSON.stringify(tagsArr));
        formData.append("pages", JSON.stringify(pages));
        formData.append("contents", pdfContents || "");

        const fileList = values.file;
        if (fileList && fileList[0] && fileList[0].originFileObj) {
            formData.append("file", fileList[0].originFileObj);
        }

        setIsSubmitting(true);
        const hide = message.loading("파일 업로드 중입니다...", 0);

        try {
            await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/proposal/upload`,
                formData,
                { headers: { "Content-Type": "multipart/form-data" } }
            );
            hide();
            setIsSubmitting(false);
            message.success(id ? "수정되었습니다." : "등록되었습니다.");
            history.push(`onandme/proposal?category=${query.get("category") || ""}`);
        } catch (err) {
            hide();
            setIsSubmitting(false);
            message.error("업로드 실패");
        }
    };

    return (
        <div className="max-w-2xl mx-auto py-8">
            <Spin spinning={isSubmitting} tip="처리 중입니다..." size="large">
                <Form
                    form={form}
                    layout="vertical"
                    className="bg-white shadow-lg p-8 rounded-xl space-y-6"
                    onFinish={handleSubmit}
                >
                    <Form.Item name="subCategory" label={<span className="font-semibold">분류</span>}>
                        <Select
                            size="large"
                            options={[
                                { value: "MICE", label: "MICE" },
                                { value: "VIDEO_PLANNING", label: "영상기획" },
                            ]}
                            className="w-full"
                        />
                    </Form.Item>

                    <Form.Item name="year" label={<span className="font-semibold">년도</span>}>
                        <Select
                            size="large"
                            options={Array.from({ length: 5 }, (_, i) => {
                                const y = new Date().getFullYear() - i;
                                return { value: y, label: y };
                            })}
                            className="w-full"
                        />
                    </Form.Item>

                    <Form.Item name="name" label={<span className="font-semibold">행사명</span>}>
                        <Input size="large" className="w-full" />
                    </Form.Item>

                    <Form.Item name="tags" label={<span className="font-semibold">태그</span>}>
                        <Input size="large" placeholder="쉼표로 구분" className="w-full" />
                    </Form.Item>

                    {/* PDF 전용 업로드, 한 개만 */}
                    <Form.Item
                        name="file"
                        label={<span className="font-semibold">PDF 업로드</span>}
                        valuePropName="fileList"
                        getValueFromEvent={normFile}
                        extra={<span className="text-gray-500 text-sm">PDF만 업로드 가능, 1개만 선택할 수 있습니다.</span>}
                        rules={[{ required: true, message: "PDF 파일을 업로드하세요" }]}
                    >
                        <Upload.Dragger
                            {...uploadProps}
                            className="!py-8 !rounded-lg"
                            maxCount={1}
                            multiple={false}
                            accept=".pdf"
                        >
                            <p className="flex justify-center text-3xl text-gray-500 mb-2"><InboxOutlined /></p>
                            <p className="text-lg font-medium">PDF 파일을 드래그하거나 클릭하세요</p>
                            <p className="text-gray-500">PDF 텍스트 추출만 진행됩니다</p>
                        </Upload.Dragger>
                    </Form.Item>

                    <Form.Item>
                        <Button
                            htmlType="submit"
                            size="large"
                            className="w-full h-14 rounded-lg text-lg font-semibold"
                            disabled={isSubmitting}
                        >
                            등록
                        </Button>
                    </Form.Item>
                </Form>
            </Spin>
        </div>
    );
}